/**
 * LayersControl: the "Layers" button on the map and its panel of reference
 * layers -- district maps (current, drafts, older maps), legislative
 * districts, census geography and tribal areas -- grouped, each with a
 * swatch that matches its line on the map.
 *
 * Lives on the map itself (next to the background switcher) so it stays
 * reachable when the side panels are collapsed, in full-map mode, and on
 * phones. The caller can put map-specific settings above the list (the plan
 * map adds the district fill slider).
 *
 * A layer whose tiles can't be found is shown disabled ("not published
 * yet") instead of failing silently. Text is translated (locales: `layers.*`);
 * staff-given district map names are shown as entered.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PMTiles } from 'pmtiles';
import { LAYER_GROUPS, layerText, type LayerDef } from '../../lib/layers';
import { useOverlayStore } from '../../stores/overlays';
import { CloseIcon, LayersIcon } from '../icons';

export type ReferenceStatus = 'loading' | 'ready' | 'missing';

const archiveProbes = new Map<string, Promise<boolean>>();

/** True when the PMTiles archive answers (header readable). Cached per URL. */
function probeArchive(url: string): Promise<boolean> {
  let probe = archiveProbes.get(url);
  if (!probe) {
    probe = new PMTiles(url).getHeader().then(
      () => true,
      () => false,
    );
    archiveProbes.set(url, probe);
  }
  return probe;
}

function Swatch({ layer }: { layer: LayerDef }): JSX.Element {
  const { color, width, dash } = layer.style;
  const w = Math.min(Math.max(width, 1.2), 3);
  return (
    <svg width={26} height={12} viewBox="0 0 26 12" aria-hidden="true" className="shrink-0">
      <line x1={1} y1={6} x2={25} y2={6} stroke="#fff" strokeWidth={w + 2.5} />
      <line
        x1={1}
        y1={6}
        x2={25}
        y2={6}
        stroke={color}
        strokeWidth={w}
        strokeDasharray={dash && dash.length ? dash.map((d) => d * w).join(' ') : undefined}
      />
    </svg>
  );
}

export function LayersControl({
  layers,
  referenceStatus,
  onToggle,
  context = 'plan',
  children,
}: {
  layers: LayerDef[];
  /** Load state of a staff-uploaded district map, by slug. */
  referenceStatus: (slug: string) => ReferenceStatus;
  onToggle: (id: string, visible: boolean) => void;
  /** Which map this is on, for the panel's note. */
  context?: 'plan' | 'coi';
  /** Extra settings shown above the layer list. */
  children?: ReactNode;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const visibility = useOverlayStore((s) => s.visibility);
  const setVisible = useOverlayStore((s) => s.setVisible);
  const [archives, setArchives] = useState<Record<string, boolean>>({});

  // Probe each PMTiles archive once so unpublished layers show as such.
  const urls = useMemo(
    () => [...new Set(layers.flatMap((l) => (l.source.type === 'pmtiles' && l.source.url ? [l.source.url] : [])))],
    [layers],
  );
  useEffect(() => {
    let cancelled = false;
    for (const url of urls) {
      void probeArchive(url).then((ok) => {
        if (!cancelled) setArchives((prev) => (prev[url] === ok ? prev : { ...prev, [url]: ok }));
      });
    }
    return () => {
      cancelled = true;
    };
  }, [urls]);

  useEffect(() => {
    if (!open) return;
    function onDown(e: PointerEvent): void {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function availability(layer: LayerDef): { ok: boolean; reason: string | null } {
    if (layer.source.type === 'reference') {
      const status = referenceStatus(layer.source.slug ?? '');
      if (status === 'loading') return { ok: false, reason: t('layers.loading') };
      if (status === 'missing') return { ok: false, reason: t('layers.loadFailed') };
      return { ok: true, reason: null };
    }
    const ok = archives[layer.source.url ?? ''];
    if (ok === false) return { ok: false, reason: t('layers.notPublished') };
    return { ok: true, reason: null };
  }

  const shown = layers.filter((l) => visibility[l.id] && availability(l).ok).length;
  const attributions = [
    ...new Set(layers.flatMap((l) => (l.attribution ? [l.attribution] : []))),
  ];

  function toggle(layer: LayerDef, value: boolean): void {
    setVisible(layer.id, value);
    onToggle(layer.id, value);
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={shown > 0 ? t('layers.buttonShown', { count: shown }) : t('layers.button')}
        title={t('layers.buttonTitle')}
        className={`floating flex h-9 items-center gap-1.5 px-2.5 text-xs font-semibold transition-colors ${
          open ? 'text-ink' : 'text-stone-600 hover:text-ink'
        }`}
      >
        <LayersIcon size={16} />
        <span className="hidden sm:inline">{t('layers.button')}</span>
        {shown > 0 && (
          <span
            className="min-w-[1.1rem] rounded-full bg-tenant-primary px-1 text-center text-[10px] font-bold leading-4 text-white"
            aria-hidden="true"
          >
            {shown}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t('layers.title')}
          className="floating absolute right-0 top-full z-40 mt-2 max-h-[min(72vh,36rem)] w-[min(21rem,calc(100vw-2rem))] overflow-y-auto p-3"
        >
          <div className="flex items-start justify-between gap-2">
            <div>
              <h2 className="text-sm font-bold text-ink">{t('layers.title')}</h2>
              <p className="mt-0.5 text-[11px] leading-snug text-stone-500">
                {context === 'coi' ? t('layers.noteCoi') : t('layers.notePlan')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t('layers.close')}
              className="-mr-1 -mt-1 rounded-lg p-1.5 text-stone-500 hover:bg-stone-100 hover:text-ink"
            >
              <CloseIcon size={16} />
            </button>
          </div>

          {children && <div className="mt-3 border-t border-stone-100 pt-3">{children}</div>}

          {layers.length === 0 && (
            <p className="mt-3 rounded-lg bg-stone-50 p-2.5 text-xs text-stone-500">{t('layers.empty')}</p>
          )}

          {LAYER_GROUPS.map((group) => {
            const items = layers.filter((l) => l.group === group);
            if (!items.length) return null;
            const groupLabel = t(`layers.groups.${group}`);
            return (
              <section key={group} className="mt-3 border-t border-stone-100 pt-3" aria-label={groupLabel}>
                <h3 className="panel-title">{groupLabel}</h3>
                <ul className="mt-1.5 space-y-0.5">
                  {items.map((layer) => {
                    const { ok, reason } = availability(layer);
                    const checked = ok && !!visibility[layer.id];
                    const text = layerText(layer, t, i18n.language);
                    return (
                      <li key={layer.id}>
                        <label
                          className={`flex items-start gap-2.5 rounded-lg px-1.5 py-1.5 ${
                            ok ? 'cursor-pointer hover:bg-stone-50' : 'cursor-not-allowed opacity-60'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!ok}
                            onChange={(e) => toggle(layer, e.target.checked)}
                            className="mt-0.5 h-4 w-4 shrink-0 rounded border-stone-300 accent-[var(--tenant-primary)]"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center justify-between gap-2">
                              <span className="text-sm font-medium leading-snug text-stone-800">{text.label}</span>
                              <Swatch layer={layer} />
                            </span>
                            <span className="mt-0.5 block text-[11px] leading-snug text-stone-500">
                              {reason ?? text.description}
                            </span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}

          {attributions.length > 0 && (
            <p className="mt-3 border-t border-stone-100 pt-2 text-[10px] leading-snug text-stone-400">
              {t('layers.sources', { list: attributions.join('; ') })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
